<template>
  <div>
    <v-progress-linear
      class="mt-5 mb-7"
      v-show="isAppointmentLoading"
      indeterminate
      rounded
    />

    <div v-show="!isAppointmentLoading">
      <!-- cancel data -->
      <v-card
        v-if="appointment.status === 'CANCELED'"
        class="pa-4 mb-5 red lighten-5 grey--text text--darken-2"
        outlined
      >
        <h3 class="red--text text--lighten-2">
          {{ $t('appointment.label.cancelled') }}
        </h3>

        <v-divider class="my-3 red lighten-4" />

        <v-row>
          <v-col cols="12" md="5">
            <h6 class="text-uppercase red--text text--lighten-2">
              {{ $t('appointment.label.dateLean') }}
            </h6>

            <span class="caption">
              {{
                appointment.date
                  ? formatDateTime(appointment.date)
                  : '---'
              }}
            </span>
          </v-col>
          <v-col cols="12" md="7">
            <h6 class="text-uppercase red--text text--lighten-2">
              {{ $t('appointment.label.origin') }}
            </h6>

            <span class="caption">
              {{
                $t(
                  `appointment.cancellationOrigin.${appointment.cancellation_origin}`
                )
              }}
            </span>
          </v-col>
        </v-row>

        <h6 class="text-uppercase red--text text--lighten-2">
          {{ $t('appointment.label.reason') }}
        </h6>

        <span class="caption">
          {{ appointment.cancel_reason }}
        </span>
      </v-card>
      <!-- /cancel data -->

      <v-row>
        <v-col cols="12" md="6">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.dateLean') }}
          </h6>
          <span class="caption">
            {{ timeSlot(date, slotSize) }}
          </span>
        </v-col>
        <v-col cols="12" md="6">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.realtyCodes') }}
          </h6>
          <div v-for="realty in appointment.realties" :key="realty.id">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-chip v-bind="attrs" v-on="on" class="mr-1 mb-1" small>
                  <small class="mw-100 text-truncate">
                    {{ realty.code }}
                  </small>
                </v-chip>
              </template>
              <span class="caption">
                {{ realty.code }}
              </span>
            </v-tooltip>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.externalCode') }}
          </h6>
          <v-chip v-if="appointment.external_code" small>
            <small>
              {{ appointment.external_code }}
            </small>
          </v-chip>
          <small v-else class="caption">---</small>
        </v-col>
        <v-col cols="12" md="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.history.label.groupCode') }}
          </h6>
          <v-chip v-if="appointment.group_code" small>
            <small>
              {{ appointment.group_code }}
            </small>
          </v-chip>
          <small v-else class="caption">---</small>
        </v-col>
        <v-col cols="12" md="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.workerType') }}
          </h6>
          <span class="caption">
            {{ appointment.worker_type ? $t(`appointment.workerType.${appointment.worker_type}`) : '---' }}
          </span>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.appointmentCode') }}
          </h6>
          <v-chip small>
            <small>
              {{ appointment.code }}
            </small>
          </v-chip>
        </v-col>
        <v-col cols="12" md="4">
          <h6 class="text-uppercase grey--text">Tipo</h6>

          <span class="caption">
            {{ $t(`type.${appointment.type}`) }}
          </span>
        </v-col>
        <v-col v-if="appointment.total_value" cols="12" md="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.value') }}
          </h6>
          <span class="caption font-weight-bold">
            {{ formatCurrency(appointment.total_value) }}
          </span>
        </v-col>
      </v-row>

      <div v-if="appointment.key_location_description" class="mt-4">
        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.keyLocation') }}
        </h6>
        <p class="caption">
          {{ appointment.key_location_description }}
        </p>
      </div>

      <v-row v-if="visibleAddressCases.includes(appointment.type)">
        <v-col>
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.meetingLocationDescription') }}
          </h6>

          <span class="caption">
            {{ appointment.meeting_location_description }}
          </span>
        </v-col>
      </v-row>

      <v-row v-if="appointment.fci">
        <v-col>
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.fci') }}
          </h6>
          <span class="caption">
            {{ formatCurrency(appointment.fci) }}
          </span>
        </v-col>
      </v-row>

      <v-row :class="`${!appointment.observations && 'mb-7'}`">
        <v-col cols="12" sm="8">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.needsAuthorization.question') }}
          </h6>

          <span class="caption">
            {{
              appointment.needs_authorization
                ? $t('appointment.label.needsAuthorization.yes')
                : $t('appointment.label.needsAuthorization.no')
            }}
          </span>
        </v-col>
        <v-col cols="12" sm="4">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.displacement') }}
          </h6>
          <span class="caption">
            {{ formatCurrency(displacement) }}
          </span>
        </v-col>
      </v-row>

      <v-row v-if="appointment.observations" class="mb-7">
        <v-col cols="12">
          <h6 class="text-uppercase grey--text">
            {{ $t('appointment.label.observations') }}
          </h6>

          <span class="caption">
            {{ appointment.observations }}
          </span>
        </v-col>
      </v-row>

      <AppointmentRealties :appointment="appointment" dense />

      <div class="mt-10" v-if="appointment.scheduled_by">
        <h3 class="grey--text text--darken-1">
          {{ $t('appointment.label.operator') }}
        </h3>

        <v-divider class="my-3 grey lighten-3" />

        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('appointment.label.name') }}
            </h6>

            <span class="caption">
              {{ appointment.scheduled_by.name }}
            </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" sm="7">
            <h6 class="text-uppercase grey--text">
              {{ $t('appointment.label.email') }}
            </h6>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  v-html="mailto(appointment.scheduled_by.username)"
                  class="mw-250 text-truncate caption"
                ></div>
              </template>

              <span class="caption">
                {{ appointment.scheduled_by.username }}
              </span>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="5">
            <h6 class="text-uppercase grey--text">
              {{ $t('appointment.label.phone') }}
            </h6>

            <span
              v-html="whatsApp(appointment.scheduled_by.phone)"
              class="caption"
            >
            </span>
          </v-col>
        </v-row>
      </div>

      <div class="mt-10" v-if="appointment.visitor_name">
        <h3 class="grey--text text--darken-1">
          {{
            isRentOut
              ? $t('appointment.label.tenantData')
              : $t('appointment.label.visitorData')
          }}
        </h3>

        <v-divider class="my-3 grey lighten-3" />

        <h6 class="text-uppercase grey--text">
          {{ $t('appointment.label.visitorName') }}
        </h6>

        <span class="caption">
          {{ appointment.visitor_name ? appointment.visitor_name : '---' }}
        </span>

        <v-row class="mt-3">
          <v-col cols="12" sm="7">
            <h6 class="text-uppercase grey--text">
              {{ $t('appointment.label.visitorMail') }}
            </h6>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="mw-250 text-truncate">
                  <span
                    class="caption"
                    v-html="mailto(appointment.visitor_mail)"
                  />
                </div>
              </template>

              {{ appointment.visitor_mail }}
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="5">
            <h6 class="text-uppercase grey--text">
              {{ $t('appointment.label.visitorPhone') }}
            </h6>
            <span
              v-if="appointment.visitor_phone"
              v-html="whatsApp(appointment.visitor_phone)"
              class="caption"
            />
            <span v-else>---</span>
          </v-col>
        </v-row>
        <v-row v-if="isRentOut">
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('appointment.formCreate.labels.visitorDocument') }}
            </h6>
            <span
              v-if="appointment.visitor_document"
              v-html="formatDocument(appointment.visitor_document)"
              class="caption"
            />
            <span v-else>---</span>
          </v-col>
          <v-col cols="5">
             <h6 class="text-uppercase grey--text">
              {{ $t('appointment.formCreate.labels.visitorBirthDate') }}
            </h6>
            <span
              v-if="appointment.visitor_birth_date"
              v-html="formatDate(appointment.visitor_birth_date)"
              class="caption"
            />
            <span v-else>---</span>
          </v-col>
        </v-row>
      </div>

      <div v-if="appointmentTermsSigned.length || appointmentTerms.length">
        <h3 class="grey--text text--darken-1 mt-8 mb-2">
          {{ $t('appointment.label.terminationTerms') }}
        </h3>

        <v-divider class="my-3 grey lighten-3" />

        <AppointmentFilesTermList
          :terms="appointmentTerms"
          :signedTerms="appointmentTermsSigned"
        />
      </div>

      <v-btn
        @click="closeAllModals"
        :to="`/appointments/${appointment.id}`"
        rel="noopener noreferrer"
        target="_blank"
        color="primary"
        elevation="0"
        class="my-8"
        outlined
        block
      >
        <span class="body-2">
          {{ $t('appointment.buttons.moreDetails') }}
        </span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import AppointmentRealties from './details/AppointmentRealties'
import AppointmentFilesTermList from './terms/AppointmentFilesTermList'

import { formatDocument } from '@/util/document'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import {
  mailto,
  whatsApp,
  catalogDate,
  catalogPhone,
  catalogMonetary,
  catalogDateWithoutTime,
  timeSlot,
} from '@/util/catalog'
import { visibleAddressCases } from './formFieldsCreate/appointmentFormDefaults'
import { rentOutTypes } from '@/components/appointment/formFieldsCreate/appointmentFormDefaults'

import Modal from '@/components/app/Modal'

export default {
  name: 'AppointmentVisualization',

  components: {
    Modal,
    AppointmentRealties,
    AppointmentFilesTermList,
  },

  data() {
    return {
      mailto,
      whatsApp,
      formatPhone: catalogPhone,
      formatDateTime: catalogDate,
      formatCurrency: catalogMonetary,
      formatDate: catalogDateWithoutTime,
      displacement: 0,
      visibleAddressCases,
      formatDocument,
      timeSlot,
    }
  },

  watch: {
    appointment(appointment) {
      if (appointment?.additionals?.length) {
        const amount = appointment?.additionals[0].value
        this.displacement = amount
      } else {
        this.displacement = 0
      }

      appointment?.id && this.fetchAppointmentFiles(appointment?.id)
    },
  },

  computed: {
    ...mapGetters([
      'user',
      'appointment',
      'appointmentTerms',
      'isAppointmentLoading',
      'appointmentTermsSigned',
    ]),

    isRentOut() {
      return rentOutTypes.includes(this.appointment.type)
    },

    date() {
      return this.appointment?.date ?? null
    },

    slotSize() {
      return this.appointment?.slot_size ?? undefined
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions([
      'fetchBoard',
      'fetchAppointment',
      'fetchAppointmentFiles',
      'rescheduleAppointment',
    ]),
  },
}
</script>
